/**
 * @fileoverview Hook for top SKUs by profit data from dashboard data.
 * @module shared/hooks/dashboard/useTopSkusByProfitData
 */

import { useMemo } from 'react'
import { useDashboardData } from './useDashboardData'
import { useLabelResolver } from '@/shared/hooks/useLabelResolver'

/**
 * Top SKU by profit data point.
 */
export interface TopSkuByProfitPoint {
  /** SKU identifier */
  id: string
  /** SKU name */
  name: string
  /** Profit amount */
  profit: number
  /** Revenue amount */
  revenue: number
  /** Advertising spend amount */
  adSpend: number
  /** Cost of goods sold amount */
  cogs: number
  /** Units sold */
  units: number
  /** Profit margin percentage */
  margin?: number | undefined
}

/**
 * Hook result for top SKUs by profit.
 */
export interface UseTopSkusByProfitResult {
  /** Top SKUs data */
  data: TopSkuByProfitPoint[]
  /** Loading state */
  isLoading: boolean
  /** Error state */
  isError: boolean
  /** Error message */
  error: string | null
}

/**
 * Hook to get top SKUs by profit data from dashboard data.
 *
 * @param limit - Maximum number of SKUs to return
 * @returns Top SKUs by profit data with loading and error states
 * @example
 * ```typescript
 * const { data, isLoading } = useTopSkusByProfitData(6);
 * ```
 */
export function useTopSkusByProfitData(limit = 6): UseTopSkusByProfitResult {
  const { salesProductQuery } = useDashboardData()
  const { productLabel } = useLabelResolver()

  const dimString = (d: Record<string, unknown> | undefined, key: string): string | undefined => {
    if (!d) return undefined
    const v = d[key as keyof typeof d]
    return typeof v === 'string' ? v : undefined
  }

  const data = useMemo(() => {
    const dataPoints = salesProductQuery.data?.data || []

    // Aggregate SKU data across all data points
    const skuMap = new Map<
      string,
      {
        id: string
        name: string
        profit: number
        revenue: number
        adSpend: number
        cogs: number
        units: number
        marginSum: number
        marginCount: number
      }
    >()

    dataPoints.forEach(dataPoint => {
      const productKey = dimString(dataPoint.dimensions as Record<string, unknown>, 'product')
      if (!productKey) return

      const id = String(productKey)
      const name = productLabel(productKey)
      const cm2 = typeof dataPoint.metrics.cm2 === 'number' ? dataPoint.metrics.cm2 : 0
      const sales = dataPoint.metrics.sales || 0
      const adSpend = dataPoint.metrics.advertising || 0
      const cogs = dataPoint.metrics.cogs || 0
      const units = dataPoint.metrics.quantity || 0
      const margin = sales > 0 ? cm2 / sales : undefined

      const existing =
        skuMap.get(id) ||
        {
          id,
          name,
          profit: 0,
          revenue: 0,
          adSpend: 0,
          cogs: 0,
          units: 0,
          marginSum: 0,
          marginCount: 0,
        }
      existing.profit += cm2
      existing.revenue += sales
      existing.adSpend += adSpend
      existing.cogs += cogs
      existing.units += units

      if (typeof margin === 'number') {
        existing.marginSum += margin
        existing.marginCount += 1
      }

      skuMap.set(id, existing)
    })

    // Convert to array and sort by profit
    const result: TopSkuByProfitPoint[] = Array.from(skuMap.values())
      .map(sku => ({
        id: sku.id,
        name: sku.name,
        profit: sku.profit,
        revenue: sku.revenue,
        adSpend: sku.adSpend,
        cogs: sku.cogs,
        units: sku.units,
        margin: sku.marginCount > 0 ? (sku.marginSum / sku.marginCount) * 100 : undefined,
      }))
      .filter(sku => sku.profit > 0)
      .sort((a, b) => b.profit - a.profit)
      .slice(0, limit)

    return result
  }, [salesProductQuery.data, productLabel, limit])

  return {
    data,
    isLoading: salesProductQuery.isLoading,
    isError: salesProductQuery.isError,
    error: salesProductQuery.error?.message || null,
  }
}
